import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { userService } from 'services/api';

const useValidationSchema = (
  shouldCheckEmailAvailability: (email: string) => boolean,
  onEmailAvailabilityCheckStarted: () => void,
  onEmailAvailabilityCheckEnded: () => void
) => {
  const { t } = useTranslation();

  const firstNameSchema = yup
    .string()
    .trim()
    .required(
      t('messages:validation.required', { field: t('fields:firstName') })
    )
    .max(
      64,
      t('messages:validation.maxLength', {
        field: t('fields:firstName'),
        length: '64',
      })
    )
    .min(
      2,
      t('messages:validation.minLength', {
        field: t('fields:firstName'),
        length: '2',
      })
    );

  const lastNameSchema = yup
    .string()
    .trim()
    .required(
      t('messages:validation.required', { field: t('fields:lastName') })
    )
    .max(
      64,
      t('messages:validation.maxLength', {
        field: t('fields:lastName'),
        length: '64',
      })
    )
    .min(
      2,
      t('messages:validation.minLength', {
        field: t('fields:lastName'),
        length: '2',
      })
    );

  const passwordSchema = yup
    .string()
    .trim()
    .test(
      'len',
      t('messages:validation.minLength', {
        field: t('fields:newPassword'),
        length: '8',
      }),
      // @ts-ignore
      (val) => val.length < 1 || val.length >= 8
    );

  const basicEmailSchema = yup
    .string()
    .trim()
    .required(t('messages:validation.required', { field: t('fields:email') }))
    .email(t('messages:validation.emailInvalid'))
    .max(
      320,
      t('messages:validation.maxLength', {
        field: t('fields:email'),
        length: '320',
      })
    );

  async function checkEmailAvailability(
    // @ts-ignore
    email
  ) {
    // check if basic schema validation passes before sending backend request
    if (!(await basicEmailSchema.isValid(email))) {
      return true;
    }
    // check additional custom condition before sending backend request
    if (!shouldCheckEmailAvailability(email)) {
      return true;
    }

    onEmailAvailabilityCheckStarted();
    try {
      return await userService.isEmailAvailable(email);
    } catch (error) {
      // @ts-ignore
      return this.createError({ message: error.message });
    } finally {
      onEmailAvailabilityCheckEnded();
    }
  }

  return yup.object({
    firstName: firstNameSchema,
    lastName: lastNameSchema,
    password: passwordSchema,
    email: basicEmailSchema.test(
      'checkEmailAvailability',
      t('messages:validation.emailUsed'),
      checkEmailAvailability
    ),
  });
};

export default useValidationSchema;
